import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../core'
import {ExpensePaymentStatus} from '../../../helpers'

import * as Yup from 'yup'
import {useFormik} from 'formik'

import {
  KTCard,
  KTCardBody,
  Select,
  ChartColumn,
  ChartDonut,
  Loading,
  Datepicker,
} from '../../../components'

import {useDispatch, useSelector} from 'react-redux'
import {
  expenseInvoiceChartCategoryGetAll,
  expenseInvoiceChartProjectGetAll,
  expenseInvoiceChartDateGetAll,
  expenseInvoiceChartMonthGetAll,
  expenseInvoiceChartStatusGetAll,
} from '../../../redux/slices/reporting.slice'
import {staffGetAll, projectLookup} from '../../../redux/slices/lookup.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'

import {
  useMemoizedValidationSchema,
  useMemoizedFormValues
} from '../../../utils/performanceOptimizations'

const selectCategoryData = (state) => ({
  category: state.category.category
})

const selectReportingData = (state) => ({
  loading: state.reporting.loading,
  expenseinvoiceCategory: state.reporting.expenseinvoiceCategory,
  expenseinvoiceProject: state.reporting.expenseinvoiceProject,
  expenseinvoiceDate: state.reporting.expenseinvoiceDate,
  expenseinvoiceMonth: state.reporting.expenseinvoiceMonth,
  expenseinvoiceStatus: state.reporting.expenseinvoiceStatus,
})

const selectLookupData = (state) => ({
  staffLookup: state.lookup.staffLookup,
  projectLookupList: state.lookup.projectLookupList
})

function Report() {
  const [categoryCategories, setCategoryCategories] = useState([])
  const [categoryData, setCategoryData] = useState([])
  const [projectProjects, setProjectProjects] = useState([])
  const [projectData, setProjectData] = useState([])
  const [monthCategories, setMonthCategories] = useState([])
  const [monthData, setMonthData] = useState([])
  const [statusCategories, setStatusCategories] = useState([])
  const [statusData, setStatusData] = useState([])
  const [dateCategories, setDateCategories] = useState([])
  const [dateData, setDateData] = useState([])
  const [dateParent, setDateParent] = useState([])

  const {category} = useSelector(selectCategoryData)
  const {
    loading,
    expenseinvoiceCategory,
    expenseinvoiceProject,
    expenseinvoiceDate,
    expenseinvoiceMonth,
    expenseinvoiceStatus,
  } = useSelector(selectReportingData)
  const {staffLookup, projectLookupList} = useSelector(selectLookupData)

  const intl = useIntl()
  const dispatch = useDispatch()

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({}), [])

  const firsDateMonth = useMemo(() => new Date().setDate(1), [])

  const formValues = useMemoizedFormValues({
    ExpenseCategoryId: '',
    PaymentStatusId: '',
    StaffId: '',
    ProjectId: '',
    StartDate: new Date(firsDateMonth),
    EndDate: new Date(),
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(expenseInvoiceChartCategoryGetAll(values))
    dispatch(expenseInvoiceChartProjectGetAll(values))
    dispatch(expenseInvoiceChartDateGetAll(values))
    dispatch(expenseInvoiceChartMonthGetAll(values))
    dispatch(expenseInvoiceChartStatusGetAll(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    dispatch(categoryGetAll({SourceTypeId: 8}))
    dispatch(staffGetAll())
    dispatch(projectLookup())
  }, [dispatch])

  const formatCurrency = useCallback((val) => {
    return intl.formatNumber(val, {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4})
  }, [intl])

  const chartTooltip = useMemo(() => ({
    y: {
      formatter: formatCurrency,
    },
  }), [formatCurrency])

  const dateChartTooltip = useMemo(() => ({
    y: {
      formatter: formatCurrency,
      title: {
        formatter: (a, data) => {
          return dateParent[data.dataPointIndex]
        },
      },
    },
  }), [formatCurrency, dateParent])

  useEffect(() => {
    if (expenseinvoiceCategory) {
      const expenseinvoiceCategoryCategories = []
      const expenseinvoiceCategoryData = []

      expenseinvoiceCategory && expenseinvoiceCategory.map((item) => {
        expenseinvoiceCategoryCategories.push(item.title)
        expenseinvoiceCategoryData.push(item.dataCount)
      })

      setCategoryCategories(expenseinvoiceCategoryCategories)
      setCategoryData(expenseinvoiceCategoryData)
    }

    if (expenseinvoiceProject) {
      const expenseinvoiceProjectProjects = []
      const expenseinvoiceProjectData = []

      expenseinvoiceProject && expenseinvoiceProject.map((item) => {
        expenseinvoiceProjectProjects.push(item.title)
        expenseinvoiceProjectData.push(item.dataCount)
      })

      setProjectProjects(expenseinvoiceProjectProjects)
      setProjectData(expenseinvoiceProjectData)
    }

    if (expenseinvoiceMonth) {
      const expenseinvoiceMonthCategories = []
      const expenseinvoiceMonthData = []

      expenseinvoiceMonth && expenseinvoiceMonth.map((item) => {
        expenseinvoiceMonthCategories.push(item.title)
        expenseinvoiceMonthData.push(item.dataCount)
      })

      setMonthCategories(expenseinvoiceMonthCategories)
      setMonthData(expenseinvoiceMonthData)
    }

    if (expenseinvoiceStatus) {
      const expenseinvoiceStatusCategories = []
      const expenseinvoiceStatusData = []

      expenseinvoiceStatus && expenseinvoiceStatus.map((item) => {
        expenseinvoiceStatusCategories.push(item.title)
        expenseinvoiceStatusData.push(item.dataCount)
      })

      setStatusCategories(expenseinvoiceStatusCategories)
      setStatusData(expenseinvoiceStatusData)
    }

    if (expenseinvoiceDate) {
      const expenseinvoiceDateCategories = []
      const expenseinvoiceDateData = []
      const expenseinvoiceDateParent = []

      expenseinvoiceDate && expenseinvoiceDate.map((item) => {
        expenseinvoiceDateCategories.push(item.title)
        expenseinvoiceDateData.push(item.dataCount)
        expenseinvoiceDateParent.push(item.parentName)
      })

      setDateCategories(expenseinvoiceDateCategories)
      setDateData(expenseinvoiceDateData)
      setDateParent(expenseinvoiceDateParent)
    }
  }, [
    expenseinvoiceCategory,
    expenseinvoiceProject,
    expenseinvoiceDate,
    expenseinvoiceMonth,
    expenseinvoiceStatus,
  ])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.REPORTING.EXPENSEINVOICE'})}</PageTitle>

      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard className='mb-5'>
            <KTCardBody className='p-9'>
              <form className='form' onSubmit={formik.handleSubmit} noValidate>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.STARTDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('StartDate')}
                          onChange={([date]) => formik.setFieldValue('StartDate', date)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.ENDDATE'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('EndDate')}
                          onChange={([date]) => formik.setFieldValue('EndDate', date)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.EXPENSECATEGORY'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={category}
                          {...formik.getFieldProps('ExpenseCategoryId')}
                          onChange={(item) =>
                            formik.setFieldValue('ExpenseCategoryId', Number(item && item.value))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.PAYMENTSTATUS'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={ExpensePaymentStatus}
                          {...formik.getFieldProps('PaymentStatusId')}
                          onChange={(item) =>
                            formik.setFieldValue('PaymentStatusId', Number(item && item.value))
                          }
                          textType={'intl'}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.STAFFID'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={staffLookup}
                          {...formik.getFieldProps('StaffId')}
                          onChange={(item) => formik.setFieldValue('StaffId', Number(item && item.value))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <div className='row mb-6'>
                      <label className='col-lg-12 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.PROJECT'})}
                      </label>
                      <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                        <Select
                          data={projectLookupList}
                          {...formik.getFieldProps('ProjectId')}
                          onChange={(item) =>
                            formik.setFieldValue('ProjectId', Number(item && item.value))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row pt-5'>
                  <div className='col-lg-12 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-kt-users-modal-action='submit'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SEARCH'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      <div className='row gy-5 g-xl-10'>
        {statusData && statusData.length > 0 && (
          <div className='col-xl-6'>
            <ChartDonut
              chartHeight='400px'
              categories={statusCategories}
              data={statusData}
              title={intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.STATUS'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {monthData && monthData.length > 0 && (
          <div className='col-xl-6'>
            <ChartColumn
              chartHeight='400px'
              categories={monthCategories}
              data={monthData}
              title={intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.MONTH'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.EXPENSEREPORT.CATEGORYTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {categoryData && categoryData.length > 0 && (
          <div className='col-xl-12'>
            <ChartColumn
              chartHeight='400px'
              categories={categoryCategories}
              data={categoryData}
              title={intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.CATEGORY'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.EXPENSEREPORT.CATEGORYTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {projectData && projectData.length > 0 && (
          <div className='col-xl-12'>
            <ChartColumn
              chartHeight='400px'
              categories={projectProjects}
              data={projectData}
              title={intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.PROJECT'})}
              tooltipTitle={intl.formatMessage({id: 'REPORT.EXPENSEREPORT.PROJECTTOOLTIP'})}
              tooltip={chartTooltip}
            />
          </div>
        )}

        {dateData && dateData.length > 0 && (
          <div className='col-xl-12'>
            <ChartColumn
              chartHeight='400px'
              categories={dateCategories}
              data={dateData}
              title={intl.formatMessage({id: 'REPORT.EXPENSEINVOICE.DATE'})}
              tooltip={dateChartTooltip}
            />
          </div>
        )}
      </div>

      {loading && <Loading />}
    </>
  )
}
export {Report}
